import { useEffect, useMemo, useRef, useState } from "react"
import {
  Building2,
  Plus,
  Search,
  Pencil,
  Trash2,
  ExternalLink,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Download,
  Upload,
  Columns3,
  RotateCcw,
  X,
} from "lucide-react"
import {
  getStudios,
  createStudio,
  updateStudio,
  deleteStudio,
  importStudios,
  type Studio,
  type StudioImportResult,
  type StudioInput,
  type PartnerTier,
} from "@/lib/api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { downloadCsv, parseCsv } from "@/lib/csv"
import { useReadOnly } from "@/lib/read-only"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { SafetyDialog } from "@/components/ConfirmDialog"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

const ACTIVITY_TYPES = ["cardio", "pilates", "recovery", "strength", "yoga"]
const PRIORITIES = ["P0", "P1", "P2"]

// Backend derives `is_partner` from partner_tier so the booking agent/app keep working.
const PARTNER_TIERS: PartnerTier[] = ["none", "non_founding", "founding"]
const PARTNER_TIER_LABEL: Record<PartnerTier, string> = {
  none: "Not a partner",
  non_founding: "Non-founding",
  founding: "Founding",
}
const PARTNER_TIER_PILL: Record<PartnerTier, string> = {
  none: "bg-gray-100 text-gray-500",
  non_founding: "bg-emerald-100 text-emerald-800",
  founding: "bg-amber-100 text-amber-800",
}
const PARTNER_TIER_RANK: Record<PartnerTier, number> = {
  none: 0,
  non_founding: 1,
  founding: 2,
}
const BOOKING_SYSTEMS = [
  "mindbody",
  "momence",
  "mariana-tek",
  "own", // studio's own / in-house system
  "in-house",
  "wellnessliving",
  "walla",
  "acuity",
  "punchpass",
  "booker",
  "tock",
  "classpass",
  "sweathouz",
  "clubready",
  "wodify",
  "kenko",
  "arketa",
  "fitdegree",
]

// API-booking provider routing keys. Must match the studios_api_provider_check
// DB constraint (and the backend StudioConfig.api_provider). "" = none (the
// studio books via the agentic flow only). Extend here + the DB CHECK together.
const API_PROVIDERS = ["mariana_tek", "momence", "mindbody", "wodify"]

// The nine optional GTM contact attributes (17 Aug studio-dashboard request).
// Declared once and reused by the form, the column registry and the CSV export
// so the three can't drift. Backend mirror: CONTACT_COLUMNS in
// backend/api/routers/admin/studios.py.
type ContactKey =
  | "primary_poc"
  | "primary_email"
  | "other_email"
  | "secondary_poc"
  | "secondary_email"
  | "tertiary_poc"
  | "tertiary_email"
  | "phone_number"
  | "comment"

const CONTACT_FIELDS: { key: ContactKey; label: string; placeholder?: string }[] = [
  { key: "primary_poc", label: "Primary POC", placeholder: "Jon Stein" },
  { key: "primary_email", label: "Primary email", placeholder: "jon@studio.com" },
  { key: "other_email", label: "Other email" },
  { key: "secondary_poc", label: "Secondary POC" },
  { key: "secondary_email", label: "Secondary email" },
  { key: "tertiary_poc", label: "Tertiary POC" },
  { key: "tertiary_email", label: "Tertiary email" },
  { key: "phone_number", label: "Phone number", placeholder: "(415) 555-0100" },
  { key: "comment", label: "Comment", placeholder: "Free-form note for the GTM team" },
]

// The three optional GTM status attributes (31 Aug studio-dashboard PRD).
// Separate from CONTACT_FIELDS because they sit in different table positions
// and picker groups (see COLUMNS below); shared with the contacts everywhere
// the form/export just needs "every GTM-owned field". Backend mirror:
// GTM_STATUS_COLUMNS in backend/api/routers/admin/studios.py.
type StatusKey = "api_status" | "api_key" | "social_media_status"

const STATUS_FIELDS: { key: StatusKey; label: string; placeholder?: string }[] = [
  { key: "api_status", label: "API status", placeholder: "Where we are in the API integration process" },
  // Deliberately NOT where a live credential goes - that is api_credential_ref,
  // which stores an env var NAME. Anything typed here is visible to every admin
  // user and lands in the CSV export.
  { key: "api_key", label: "API key", placeholder: "Partner reference - never a live secret" },
  { key: "social_media_status", label: "Social media status", placeholder: "Where we are in the social media process" },
]

const GTM_FIELDS = [...CONTACT_FIELDS, ...STATUS_FIELDS]

const EMPTY_GTM: Pick<StudioInput, ContactKey | "api_status" | "social_media_status"> = {
  primary_poc: "",
  primary_email: "",
  other_email: "",
  secondary_poc: "",
  secondary_email: "",
  tertiary_poc: "",
  tertiary_email: "",
  phone_number: "",
  comment: "",
  api_status: "",
  social_media_status: "",
}

// Blank stays blank on the way out: the backend turns "" into NULL (see
// _trim_contact), so the form never has to distinguish the two. api_key is
// handled separately - never prefilled from GET responses.
function gtmFieldsOf(s: Partial<Studio>): Pick<StudioInput, ContactKey | "api_status" | "social_media_status"> {
  const out = { ...EMPTY_GTM }
  for (const { key } of GTM_FIELDS) {
    if (key === "api_key") continue
    out[key] = s[key] ?? ""
  }
  return out
}

const EMPTY_FORM: Partial<StudioInput> = {
  ...EMPTY_GTM,
  name: "",
  activity_type: "yoga",
  is_active: true,
  city: "San Francisco",
  url: "",
  image_url: "",
  location_pill: "",
  neighborhood: "",
  address: "",
  logo_url: "",
  booking_method: "agentic", // vestigial; defaulted server-side, not shown on the form
  booking_system: "",
  venue_id: "",
  is_partner: false, // derived server-side from partner_tier; not shown on the form
  partner_tier: "none",
  priority: "P0",
  display_order: null,
  api_provider: "",
  api_location_id: "",
  api_credential_ref: "",
  api_booking_enabled: false,
  // Left blank on create — the backend derives a correct IANA tz from `city`
  // (see _default_timezone_for_city) so a studio in any city gets a working
  // default without the admin having to know the zone name.
  timezone: "",
}

// Kept in sync with backend StudioCreate.
const REQUIRED_FIELDS: { key: keyof StudioInput; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "activity_type", label: "Activity type" },
  { key: "booking_system", label: "Booking system" },
  { key: "url", label: "Booking URL" },
  { key: "address", label: "Address" },
]

type SortKey =
  | "name"
  | "activity_type"
  | "booking_system"
  | "priority"
  | "partner_tier"
  | "is_active"
  | "display_order"
  | "city"
  | "neighborhood"
  | "timezone"

// Short display form for a studio's timezone. Shown on EVERY row: the old code
// hid the badge whenever the value was Pacific, which is exactly how two New
// York studios sat on America/Los_Angeles unnoticed - the bug hid itself. The
// zone pins the booking agent's browser clock, so it is always worth seeing.
const TZ_ABBREV: Record<string, string> = {
  "America/Los_Angeles": "PT",
  "America/Denver": "MT",
  "America/Phoenix": "MT",
  "America/Chicago": "CT",
  "America/New_York": "ET",
}

// Mirrors studio-health.tsx so the Scrape pill reads identically on both screens.
const MODE_LABEL: Record<string, string> = {
  catalog: "Catalog",
  login_required: "Login required",
  live_discovery: "Live discovery",
  appointment: "Appointment",
}
const MODE_COLOR: Record<string, string> = {
  catalog: "bg-emerald-100 text-emerald-800",
  login_required: "bg-amber-100 text-amber-800",
  appointment: "bg-blue-100 text-blue-800",
  live_discovery: "bg-gray-100 text-gray-700",
}

function activityColor(type: string): string {
  switch (type) {
    case "yoga":
      return "bg-purple-100 text-purple-800"
    case "cardio":
      return "bg-red-100 text-red-800"
    case "pilates":
      return "bg-pink-100 text-pink-800"
    case "strength":
      return "bg-blue-100 text-blue-800"
    case "recovery":
      return "bg-green-100 text-green-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

// --- column registry ---------------------------------------------------------
// One entry per selectable column. The array order IS the table order, so the
// default view reads exactly as it always did and every column the GTM team
// switches on lands in a fixed, predictable slot (never jumping to wherever it
// was clicked). Handlers arrive via `ctx` so the registry stays module-level.
type ColumnCtx = {
  readOnly: boolean
  onCycleTier: (s: Studio) => void
  onToggleActive: (s: Studio) => void
  onEdit: (s: Studio) => void
  onDelete: (s: Studio) => void
}

type ColumnGroup = "core" | "location" | "booking" | "contacts"

type ColumnDef = {
  key: string
  label: string
  group: ColumnGroup
  /** Omitted for columns with no meaningful ordering (e.g. the link icon). */
  sortKey?: SortKey
  /** Always rendered; not offered in the column picker. */
  pinned?: boolean
  headerClass?: string
  render: (s: Studio, ctx: ColumnCtx) => React.ReactNode
}

// A cell that can hold a long free-text value. Truncated so one studio with a
// paragraph in `comment` can't blow the table width out; full text on hover.
function TextCell({ value }: { value?: string | null }) {
  const v = (value ?? "").trim()
  if (!v) return <span className="text-muted-foreground">—</span>
  return (
    <div className="max-w-[220px] truncate" title={v}>
      {v}
    </div>
  )
}

function EmailCell({ value }: { value?: string | null }) {
  const v = (value ?? "").trim()
  if (!v) return <span className="text-muted-foreground">—</span>
  return (
    <a
      href={`mailto:${v}`}
      className="block max-w-[220px] truncate text-blue-600 hover:underline"
      title={v}
    >
      {v}
    </a>
  )
}

const COLUMNS: ColumnDef[] = [
  {
    key: "name",
    label: "Name",
    group: "core",
    sortKey: "name",
    pinned: true,
    render: (s) => (
      <>
        <div className="font-medium">{s.name}</div>
        <div className="text-xs text-muted-foreground">
          {[s.location_pill, s.city].filter(Boolean).join(" · ") || "—"}
          {s.timezone && (
            <span
              className={cn(
                "ml-1.5 rounded px-1 py-0.5 text-[10px] font-medium",
                s.timezone === "America/Los_Angeles"
                  ? "bg-gray-100 text-gray-600"
                  : "bg-amber-100 text-amber-800",
              )}
              title={s.timezone}
            >
              {TZ_ABBREV[s.timezone] ?? s.timezone}
            </span>
          )}
        </div>
      </>
    ),
  },
  {
    key: "activity_type",
    label: "Activity",
    group: "core",
    sortKey: "activity_type",
    render: (s) => (
      <Badge className={cn("font-normal", activityColor(s.activity_type))}>
        {s.activity_type}
      </Badge>
    ),
  },
  {
    key: "booking_system",
    label: "Booking system",
    group: "core",
    sortKey: "booking_system",
    render: (s) => (
      <>
        <div>{s.booking_system}</div>
        {s.api_provider && (
          <span
            className={cn(
              "mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium",
              s.api_booking_enabled
                ? "bg-emerald-100 text-emerald-800"
                : "bg-amber-100 text-amber-800",
            )}
            title={
              s.api_booking_enabled
                ? "API booking enabled"
                : "API mapped, not yet enabled"
            }
          >
            api: {s.api_provider}
          </span>
        )}
      </>
    ),
  },
  {
    key: "api_status",
    label: "API status",
    group: "booking",
    render: (s) => <TextCell value={s.api_status} />,
  },
  {
    key: "api_key",
    label: "API key",
    group: "booking",
    render: (s) =>
      s.api_key_set ? (
        <span className="text-sm">Set</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: "scrape",
    label: "Scrape",
    group: "core",
    render: (s) => <ScrapeCell studio={s} />,
  },
  {
    key: "priority",
    label: "Priority",
    group: "core",
    sortKey: "priority",
    render: (s) => s.priority,
  },
  {
    key: "partner_tier",
    label: "Partner",
    group: "core",
    sortKey: "partner_tier",
    render: (s, ctx) => (
      <button
        onClick={() => ctx.onCycleTier(s)}
        disabled={ctx.readOnly}
        className={cn(
          "rounded-full px-2 py-0.5 text-xs font-medium",
          PARTNER_TIER_PILL[s.partner_tier ?? "none"],
          ctx.readOnly && "cursor-not-allowed opacity-60",
        )}
        title={ctx.readOnly ? "Read-only mode" : "Click to cycle partner tier"}
      >
        {PARTNER_TIER_LABEL[s.partner_tier ?? "none"]}
      </button>
    ),
  },
  {
    key: "is_active",
    label: "Active",
    group: "core",
    sortKey: "is_active",
    render: (s, ctx) => (
      <button
        onClick={() => ctx.onToggleActive(s)}
        disabled={ctx.readOnly}
        className={cn(
          "rounded-full px-2 py-0.5 text-xs font-medium",
          s.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600",
          ctx.readOnly && "cursor-not-allowed opacity-60",
        )}
        title={ctx.readOnly ? "Read-only mode" : "Toggle active"}
      >
        {s.is_active ? "active" : "inactive"}
      </button>
    ),
  },
  {
    key: "link",
    label: "Link",
    group: "core",
    render: (s) =>
      s.url ? (
        <a
          href={s.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  // --- off by default -------------------------------------------------------
  {
    key: "city",
    label: "City",
    group: "location",
    sortKey: "city",
    render: (s) => <TextCell value={s.city} />,
  },
  {
    key: "neighborhood",
    label: "Neighborhood",
    group: "location",
    sortKey: "neighborhood",
    render: (s) => <TextCell value={s.neighborhood} />,
  },
  {
    key: "timezone",
    label: "Timezone",
    group: "location",
    sortKey: "timezone",
    render: (s) => <TextCell value={s.timezone} />,
  },
  {
    key: "address",
    label: "Address",
    group: "location",
    render: (s) => <TextCell value={s.address} />,
  },
  {
    key: "venue_id",
    label: "Venue ID",
    group: "booking",
    render: (s) => <TextCell value={s.venue_id} />,
  },
  {
    key: "display_order",
    label: "Order",
    group: "booking",
    sortKey: "display_order",
    render: (s) => (
      <span className="tabular-nums">{s.display_order ?? "—"}</span>
    ),
  },
  {
    key: "api_provider",
    label: "API provider",
    group: "booking",
    render: (s) => <TextCell value={s.api_provider} />,
  },
  {
    key: "social_media_status",
    label: "Social media status",
    group: "contacts",
    render: (s) => <TextCell value={s.social_media_status} />,
  },
  ...CONTACT_FIELDS.map<ColumnDef>(({ key, label }) => ({
    key,
    label,
    group: "contacts",
    render: (s: Studio) =>
      key.endsWith("email") ? (
        <EmailCell value={s[key]} />
      ) : (
        <TextCell value={s[key]} />
      ),
  })),
  {
    key: "actions",
    label: "Actions",
    group: "core",
    pinned: true,
    headerClass: "text-right",
    render: (s, ctx) => (
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon" onClick={() => ctx.onEdit(s)} title="Edit">
          <Pencil className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => ctx.onDelete(s)}
          disabled={ctx.readOnly}
          title="Delete"
        >
          <Trash2 className="size-4 text-red-600" />
        </Button>
      </div>
    ),
  },
]

const COLUMN_KEYS = new Set(COLUMNS.map((c) => c.key))

// The view the dashboard has always shown. "Reset to default view" returns here.
const DEFAULT_VISIBLE: string[] = COLUMNS.filter((c) => c.group === "core").map(
  (c) => c.key,
)

const GROUP_LABEL: Record<ColumnGroup, string> = {
  core: "Default",
  location: "Location",
  booking: "Booking",
  contacts: "Contacts",
}

const COLUMNS_STORAGE_KEY = "offseason-admin-studio-columns"

function loadVisibleColumns(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY) ?? "null")
    if (!Array.isArray(raw)) return new Set(DEFAULT_VISIBLE)
    // Drop keys from an older build so a renamed column can't strand the table
    // on a set of columns that no longer exist.
    const known = raw.filter((k) => typeof k === "string" && COLUMN_KEYS.has(k))
    return known.length > 0 ? new Set(known) : new Set(DEFAULT_VISIBLE)
  } catch {
    return new Set(DEFAULT_VISIBLE)
  }
}

function persistVisibleColumns(keys: Set<string>) {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify([...keys]))
  } catch {
    /* storage full/blocked */
  }
}

/** A new Set with `value` added or removed. Filters stay plain Sets; nothing
 *  here is big enough to justify a reducer or a filter store. */
function toggled(set: Set<string>, value: string): Set<string> {
  const next = new Set(set)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

// Row-level filter. `activities === null` skips the activity test, which is how
// the by-activity tiles keep showing real counts while an activity is selected
// (otherwise every unselected tile would read 0).
function matchesFilters(
  s: Studio,
  q: string,
  cities: Set<string>,
  tiers: Set<string>,
  activities: Set<string> | null,
): boolean {
  if (activities && activities.size > 0 && !activities.has(s.activity_type)) return false
  if (tiers.size > 0 && !tiers.has(s.partner_tier ?? "none")) return false
  if (cities.size > 0 && !cities.has((s.city ?? "").trim())) return false
  if (!q) return true
  return (
    s.name.toLowerCase().includes(q) ||
    (s.city ?? "").toLowerCase().includes(q) ||
    (s.booking_system ?? "").toLowerCase().includes(q) ||
    (s.location_pill ?? "").toLowerCase().includes(q)
  )
}

export function StudiosPage() {
  const { readOnly } = useReadOnly()
  const [studios, setStudios] = useState<Studio[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  // Multi-select filters. An EMPTY set means "no filter", not "nothing shown" -
  // that way a fresh page and a fully-cleared page are the same state.
  const [activityFilter, setActivityFilter] = useState<Set<string>>(new Set())
  const [tierFilter, setTierFilter] = useState<Set<string>>(new Set())
  const [cityFilter, setCityFilter] = useState<Set<string>>(new Set())

  // Column config is per-admin and survives reloads; it is NOT part of the
  // filter state, so "Clear all filters" and "Reset to default view" stay
  // independent controls (GTM request items 3 and 7).
  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadVisibleColumns)
  const [columnsOpen, setColumnsOpen] = useState(false)

  function toggleColumn(key: string) {
    setVisibleCols((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      persistVisibleColumns(next)
      return next
    })
  }

  function resetColumns() {
    const next = new Set(DEFAULT_VISIBLE)
    persistVisibleColumns(next)
    setVisibleCols(next)
  }

  const columns = useMemo(
    () => COLUMNS.filter((c) => c.pinned || visibleCols.has(c.key)),
    [visibleCols],
  )

  const filterCount =
    activityFilter.size + tierFilter.size + cityFilter.size + (search.trim() ? 1 : 0)

  function clearFilters() {
    setSearch("")
    setActivityFilter(new Set())
    setTierFilter(new Set())
    setCityFilter(new Set())
  }

  const [sortKey, setSortKey] = useState<SortKey>("display_order")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  const [editing, setEditing] = useState<Partial<Studio> | null>(null)
  const [form, setForm] = useState<Partial<StudioInput>>(EMPTY_FORM)
  const [apiKeyDraft, setApiKeyDraft] = useState("")
  const [apiKeyRemove, setApiKeyRemove] = useState(false)
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<Studio | null>(null)
  const [deleting, setDeleting] = useState(false)

  // CSV import. The parsed rows are held so the Apply click re-posts EXACTLY
  // what was previewed - re-parsing (or re-planning against a table that moved
  // underneath) would let the confirmed diff differ from the written one.
  const importFileRef = useRef<HTMLInputElement>(null)
  const [importRows, setImportRows] = useState<Record<string, string>[]>([])
  const [importPreview, setImportPreview] = useState<StudioImportResult | null>(null)
  const [importing, setImporting] = useState(false)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await getStudios()
      setStudios(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load studios")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return studios.filter((s) =>
      matchesFilters(s, q, cityFilter, tierFilter, activityFilter),
    )
  }, [studios, search, activityFilter, tierFilter, cityFilter])

  // Same filters minus the activity one, so the by-activity tiles show what
  // clicking each would actually give you.
  const activityBase = useMemo(() => {
    const q = search.trim().toLowerCase()
    return studios.filter((s) => matchesFilters(s, q, cityFilter, tierFilter, null))
  }, [studios, search, tierFilter, cityFilter])

  const cityOptions = useMemo(() => {
    const seen = new Set<string>()
    for (const s of studios) {
      const c = (s.city ?? "").trim()
      if (c) seen.add(c)
    }
    return [...seen].sort((a, b) => a.localeCompare(b))
  }, [studios])

  const sorted = useMemo(() => {
    const valueOf = (s: Studio): string | number => {
      switch (sortKey) {
        case "partner_tier":
          return PARTNER_TIER_RANK[s.partner_tier ?? "none"]
        case "is_active":
          return s.is_active ? 1 : 0
        case "display_order":
          return s.display_order ?? Number.MAX_SAFE_INTEGER
        default:
          return (s[sortKey] ?? "").toString().toLowerCase()
      }
    }
    const dir = sortDir === "asc" ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      let cmp: number
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv
      } else {
        cmp = String(av).localeCompare(String(bv))
      }
      // Stable secondary sort by name so rows in the same group (e.g. all
      // "yoga") stay in a predictable order rather than jittering.
      if (cmp === 0) cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      return cmp * dir
    })
  }, [filtered, sortKey, sortDir])

  function openCreate() {
    setForm({ ...EMPTY_FORM })
    setApiKeyDraft("")
    setApiKeyRemove(false)
    setEditing({}) // truthy, no id → create
  }

  function openEdit(s: Studio) {
    setForm({
      ...gtmFieldsOf(s),
      name: s.name,
      activity_type: s.activity_type,
      is_active: s.is_active,
      city: s.city ?? "",
      url: s.url ?? "",
      image_url: s.image_url ?? "",
      location_pill: s.location_pill ?? "",
      neighborhood: s.neighborhood ?? "",
      address: s.address ?? "",
      logo_url: s.logo_url ?? "",
      booking_method: s.booking_method,
      booking_system: s.booking_system,
      venue_id: s.venue_id ?? "",
      is_partner: s.is_partner,
      partner_tier: s.partner_tier ?? "none",
      priority: s.priority,
      display_order: s.display_order,
      api_provider: s.api_provider ?? "",
      api_location_id: s.api_location_id ?? "",
      api_credential_ref: s.api_credential_ref ?? "",
      api_booking_enabled: s.api_booking_enabled,
      timezone: s.timezone ?? "",
    })
    setApiKeyDraft("")
    setApiKeyRemove(false)
    setEditing(s)
  }

  function setField<K extends keyof StudioInput>(key: K, value: StudioInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function payload(): Partial<StudioInput> {
    const nullIfBlank = (v: unknown) =>
      typeof v === "string" && v.trim() === "" ? null : v
    const out: Partial<StudioInput> = {
      ...form,
      name: (form.name ?? "").trim(),
      city: nullIfBlank(form.city) as string | null,
      url: nullIfBlank(form.url) as string | null,
      image_url: nullIfBlank(form.image_url) as string | null,
      location_pill: nullIfBlank(form.location_pill) as string | null,
      neighborhood: nullIfBlank(form.neighborhood) as string | null,
      address: nullIfBlank(form.address) as string | null,
      logo_url: nullIfBlank(form.logo_url) as string | null,
      venue_id: nullIfBlank(form.venue_id) as string | null,
      // api_provider MUST be null (not "") when unset — the DB CHECK only
      // allows the known providers or NULL, so an empty string would 400.
      api_provider: nullIfBlank(form.api_provider) as string | null,
      api_location_id: nullIfBlank(form.api_location_id) as string | null,
      api_credential_ref: nullIfBlank(form.api_credential_ref) as string | null,
      // timezone is NOT NULL in the DB — left blank on create so the backend
      // can derive it from city; trimmed (not nulled) here so an edit can
      // never send an invalid null.
      timezone: (form.timezone ?? "").trim(),
    }
    if (apiKeyRemove) {
      out.api_key = ""
    } else if (apiKeyDraft.trim()) {
      out.api_key = apiKeyDraft.trim()
    }
    return out
  }

  async function handleSave() {
    const missing = REQUIRED_FIELDS.filter((f) => {
      const v = form[f.key]
      return v == null || (typeof v === "string" && v.trim() === "")
    })
    if (missing.length > 0) {
      toast.error(`Required: ${missing.map((f) => f.label).join(", ")}`)
      return
    }
    setSaving(true)
    try {
      if (editing && (editing as Studio).id) {
        const updated = await updateStudio((editing as Studio).id, payload())
        setStudios((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        )
        toast.success(`Updated ${updated.name}`)
      } else {
        const created = await createStudio(payload())
        setStudios((prev) => [...prev, created])
        toast.success(`Added ${created.name}`)
      }
      setEditing(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save studio")
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleActive(s: Studio) {
    try {
      const updated = await updateStudio(s.id, { is_active: !s.is_active })
      setStudios((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      toast.success(`${updated.name} ${updated.is_active ? "activated" : "deactivated"}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to toggle studio")
    }
  }

  // Inline partner-tier cycle. Clicking the pill advances none → non-founding →
  // founding → none and PATCHes partner_tier straight to Supabase (the backend
  // re-derives is_partner), so changing tier persists without opening the form.
  async function handleCyclePartnerTier(s: Studio) {
    const current = s.partner_tier ?? "none"
    const next =
      PARTNER_TIERS[(PARTNER_TIER_RANK[current] + 1) % PARTNER_TIERS.length]
    try {
      const updated = await updateStudio(s.id, { partner_tier: next })
      setStudios((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      toast.success(
        `${updated.name} → ${PARTNER_TIER_LABEL[updated.partner_tier ?? "none"]}`,
      )
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update partner tier")
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteStudio(deleteTarget.id)
      setStudios((prev) => prev.filter((s) => s.id !== deleteTarget.id))
      toast.success(`Deleted ${deleteTarget.name}`)
      setDeleteTarget(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete studio")
    } finally {
      setDeleting(false)
    }
  }

  // Stats describe the CURRENT SELECTION, not the whole catalog (GTM request
  // item 2): filter to Berkeley and the tiles answer "how many Berkeley
  // studios, how many of those are partners".
  const stats = useMemo(() => {
    const byActivity: Record<string, number> = {}
    for (const t of ACTIVITY_TYPES) byActivity[t] = 0
    let other = 0
    for (const s of activityBase) {
      if (s.activity_type in byActivity) byActivity[s.activity_type] += 1
      else other += 1
    }
    return {
      total: filtered.length,
      // Count partner_tier (the source of truth the table pills render), NOT
      // the derived is_partner boolean — legacy rows where is_partner was set
      // directly in Supabase would otherwise make the tile disagree with the list.
      partners: filtered.filter((s) => (s.partner_tier ?? "none") !== "none").length,
      active: filtered.filter((s) => s.is_active).length,
      byActivity,
      other,
    }
  }, [filtered, activityBase])

  // Parse in the browser and ask the backend what the sheet WOULD do. Nothing
  // is written until the preview dialog's Apply. Same shape as the waitlist
  // import: no file is uploaded, only parsed rows as JSON.
  async function onImportFile(file: File) {
    setImporting(true)
    try {
      const { rows } = parseCsv(await file.text())
      if (rows.length === 0) {
        toast.error("That CSV has no data rows")
        return
      }
      const preview = await importStudios(rows, false)
      setImportRows(rows)
      setImportPreview(preview)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not read that CSV")
    } finally {
      setImporting(false)
    }
  }

  async function applyImport() {
    setImporting(true)
    try {
      const result = await importStudios(importRows, true)
      const failed = result.failed?.length ?? 0
      const added = result.created > 0 ? `, added ${result.created} new` : ""
      if (failed > 0) {
        toast.error(`Updated ${result.written} studios${added}, ${failed} failed - see the console`)
        console.error("Studio import failures", result.failed)
      } else {
        toast.success(
          `Updated ${result.written} ${result.written === 1 ? "studio" : "studios"}${added}`,
        )
      }
      setImportPreview(null)
      setImportRows([])
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Import failed")
    } finally {
      setImporting(false)
    }
  }

  const exportGtmFields = GTM_FIELDS.filter((f) => f.key !== "api_key")

  const exportStudios = () =>
    downloadCsv(
      "studios.csv",
      [
        "name", "activity", "city", "neighborhood", "locationPill", "timezone",
        "address", "bookingSystem", "bookingUrl", "priority", "partnerTier",
        "active", "apiProvider", "apiBookingEnabled", "venueId", "displayOrder",
        ...exportGtmFields.map((f) => f.key),
      ],
      sorted.map((s) => [
        s.name, s.activity_type, s.city ?? "", s.neighborhood ?? "",
        s.location_pill ?? "", s.timezone ?? "", s.address ?? "",
        s.booking_system ?? "", s.url ?? "", s.priority ?? "",
        s.partner_tier ?? "none", s.is_active ? "yes" : "no", s.api_provider ?? "",
        s.api_booking_enabled ? "yes" : "no", s.venue_id ?? "",
        s.display_order ?? "",
        ...exportGtmFields.map((f) => (f.key === "api_key" ? "" : (s[f.key] ?? ""))),
      ]),
    )

  // Handlers the column renderers need. Rebuilt each render (cheap - no
  // memoized children downstream).
  const columnCtx: ColumnCtx = {
    readOnly,
    onCycleTier: (s) => void handleCyclePartnerTier(s),
    onToggleActive: (s) => void handleToggleActive(s),
    onEdit: openEdit,
    onDelete: setDeleteTarget,
  }

  const isEdit = Boolean(editing && (editing as Studio).id)

  return (
    <div className="space-y-5 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Building2 className="size-6" /> Studios
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            The booking agent and mobile app read this list from Supabase. Edits
            here go straight to the <code>studios</code> table and take effect within ~60s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn("size-4", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportStudios}
            disabled={loading || sorted.length === 0}
            title="Download the table as shown (filters + sort applied) as CSV"
          >
            <Download className="size-4" /> Export CSV
          </Button>
          <input
            ref={importFileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void onImportFile(f)
              e.target.value = ""
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => importFileRef.current?.click()}
            disabled={readOnly || importing}
            title="Merge a GTM spreadsheet into these rows — preview first, nothing is written until you confirm"
          >
            <Upload className="size-4" /> Import CSV
          </Button>
          <Button size="sm" onClick={openCreate} disabled={readOnly}>
            <Plus className="size-4" /> Add Studio
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3 sm:max-w-md">
        <Stat label={filterCount > 0 ? "Studios (filtered)" : "Studios"} value={stats.total} />
        <Stat label="Partners" value={stats.partners} />
        <Stat label="Active" value={stats.active} />
      </div>

      {/* By-activity breakdown — surfaces catalog imbalances at a glance.
          Each tile filters the table to that activity (click again to clear). */}
      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          By activity
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {ACTIVITY_TYPES.map((t) => {
            const selected = activityFilter.has(t)
            return (
              <button
                key={t}
                onClick={() => setActivityFilter(toggled(activityFilter, t))}
                className={cn(
                  "rounded-lg border bg-card px-4 py-3 text-left transition hover:bg-muted/40",
                  selected && "ring-2 ring-ring",
                )}
                title={selected ? `Stop filtering on ${t}` : `Add ${t} to the modality filter`}
              >
                <div className="text-2xl font-bold tabular-nums">
                  {stats.byActivity[t] ?? 0}
                </div>
                <Badge className={cn("mt-1 font-normal capitalize", activityColor(t))}>
                  {t}
                </Badge>
              </button>
            )
          })}
          {stats.other > 0 && (
            <div className="rounded-lg border bg-card px-4 py-3">
              <div className="text-2xl font-bold tabular-nums">{stats.other}</div>
              <Badge className={cn("mt-1 font-normal", activityColor("other"))}>
                other
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, city, booking system…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <MultiSelect
          label="City"
          options={cityOptions.map((c) => ({ value: c, label: c }))}
          selected={cityFilter}
          onToggle={(v) => setCityFilter(toggled(cityFilter, v))}
          onClear={() => setCityFilter(new Set())}
        />
        <MultiSelect
          label="Partner status"
          options={PARTNER_TIERS.map((t) => ({
            value: t,
            label: PARTNER_TIER_LABEL[t],
          }))}
          selected={tierFilter}
          onToggle={(v) => setTierFilter(toggled(tierFilter, v))}
          onClear={() => setTierFilter(new Set())}
        />
        <MultiSelect
          label="Modality"
          options={ACTIVITY_TYPES.map((t) => ({ value: t, label: t }))}
          selected={activityFilter}
          onToggle={(v) => setActivityFilter(toggled(activityFilter, v))}
          onClear={() => setActivityFilter(new Set())}
        />
        {filterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="size-4" /> Clear all filters
          </Button>
        )}
        <Button
          variant={columnsOpen ? "secondary" : "outline"}
          size="sm"
          onClick={() => setColumnsOpen((o) => !o)}
          title="Choose which columns the table shows"
        >
          <Columns3 className="size-4" /> Columns
        </Button>
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          Showing {filtered.length} of {studios.length}
        </span>
      </div>

      {/* Body */}
      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Loading studios…</div>
      ) : (
        <div className="flex items-start gap-4">
          {columnsOpen && (
            <ColumnPanel
              visible={visibleCols}
              onToggle={toggleColumn}
              onReset={resetColumns}
              onClose={() => setColumnsOpen(false)}
            />
          )}
          <div className="min-w-0 flex-1 overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                {columns.map((c) =>
                  c.sortKey ? (
                    <SortHeader
                      key={c.key}
                      label={c.label}
                      col={c.sortKey}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                    />
                  ) : (
                    <th
                      key={c.key}
                      className={cn("px-3 py-2 font-medium", c.headerClass)}
                    >
                      {c.label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <tr key={s.id} className="border-t hover:bg-muted/30">
                  {columns.map((c) => (
                    <td key={c.key} className={cn("px-3 py-2", c.headerClass)}>
                      {c.render(s, columnCtx)}
                    </td>
                  ))}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-8 text-center text-muted-foreground"
                  >
                    No studios match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit Studio" : "Add Studio"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name *" className="sm:col-span-2">
              <Input
                value={form.name ?? ""}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Body Temp Yoga"
              />
            </Field>

            <Field label="Activity type *">
              <Select
                value={form.activity_type ?? ""}
                onChange={(v) => setField("activity_type", v)}
                options={ACTIVITY_TYPES}
              />
            </Field>

            <Field label="Booking system *">
              <Select
                value={form.booking_system ?? ""}
                onChange={(v) => setField("booking_system", v)}
                options={BOOKING_SYSTEMS}
                placeholder="Select a booking system…"
              />
            </Field>

            <Field label="Booking URL *" className="sm:col-span-2">
              <Input
                value={form.url ?? ""}
                onChange={(e) => setField("url", e.target.value)}
                placeholder="https://…/schedule"
              />
              <p className="text-xs text-muted-foreground">
                The scraper auto-detects this studio's platform from this URL and
                starts pulling classes on the next scrape. No scrape config
                needed here.
              </p>
            </Field>

            <Field label="Priority">
              <Select
                value={form.priority ?? ""}
                onChange={(v) => setField("priority", v)}
                options={PRIORITIES}
              />
            </Field>

            <Field label="Venue ID">
              <Input
                value={form.venue_id ?? ""}
                onChange={(e) => setField("venue_id", e.target.value)}
                placeholder="(MindBody site / venue id)"
              />
            </Field>

            <Field label="Display order">
              <Input
                type="number"
                value={form.display_order ?? ""}
                onChange={(e) =>
                  setField(
                    "display_order",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="auto (end of list)"
              />
            </Field>

            <Field label="City">
              <Input
                value={form.city ?? ""}
                onChange={(e) => setField("city", e.target.value)}
                placeholder="San Francisco"
              />
            </Field>

            <Field label="Location pill">
              <Input
                value={form.location_pill ?? ""}
                onChange={(e) => setField("location_pill", e.target.value)}
                placeholder="Union Street"
              />
            </Field>

            <Field label="Neighborhood">
              <Input
                value={form.neighborhood ?? ""}
                onChange={(e) => setField("neighborhood", e.target.value)}
                placeholder="Cow Hollow"
              />
            </Field>

            {/* Read-only on purpose. A trigger on `studios` derives this from
                city on every write (sql/studios_timezone_trigger.sql), so a
                hand-typed value here would just be overwritten - and a stale
                one silently points the booking agent's browser at the wrong
                region. Change the City field to change the zone. */}
            <Field label="Timezone">
              <Input
                value={form.timezone ?? ""}
                readOnly
                tabIndex={-1}
                className="cursor-not-allowed bg-muted text-muted-foreground"
                placeholder="derived from City"
              />
              <p className="text-xs text-muted-foreground">
                Set automatically from City. Fix the city to fix the zone.
              </p>
            </Field>

            <Field label="Address *" className="sm:col-span-2">
              <Input
                value={form.address ?? ""}
                onChange={(e) => setField("address", e.target.value)}
                placeholder="2310 Union St, San Francisco, CA 94123"
              />
            </Field>

            <Field label="Image URL">
              <Input
                value={form.image_url ?? ""}
                onChange={(e) => setField("image_url", e.target.value)}
                placeholder="https://…"
              />
            </Field>

            <Field label="Logo URL">
              <Input
                value={form.logo_url ?? ""}
                onChange={(e) => setField("logo_url", e.target.value)}
                placeholder="https://…"
              />
            </Field>

            <Field label="Partner tier">
              <select
                value={form.partner_tier ?? "none"}
                onChange={(e) =>
                  setField("partner_tier", e.target.value as PartnerTier)
                }
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {PARTNER_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {PARTNER_TIER_LABEL[t]}
                  </option>
                ))}
              </select>
            </Field>

            {/* API booking (optional) - for the future direct-API path. Leave
                blank to keep the studio on the agentic flow. */}
            <div className="mt-2 border-t pt-4 sm:col-span-2">
              <div className="text-sm font-semibold">API booking</div>
              <p className="text-xs text-muted-foreground">
                Optional. Used by the future direct-API booking path. The studio
                keeps using the agentic flow until "API booking enabled" is on.
              </p>
            </div>

            <Field label="API provider">
              <select
                value={form.api_provider ?? ""}
                onChange={(e) => setField("api_provider", e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">None (agentic only)</option>
                {API_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="API location ID">
              <Input
                value={form.api_location_id ?? ""}
                onChange={(e) => setField("api_location_id", e.target.value)}
                placeholder="provider site / location id"
              />
            </Field>

            <Field label="API credential ref" className="sm:col-span-2">
              <Input
                value={form.api_credential_ref ?? ""}
                onChange={(e) => setField("api_credential_ref", e.target.value)}
                placeholder="env var NAME (e.g. MARIANA_TEK_API_KEY) - never the secret"
              />
            </Field>

            {/* Studio relationship contacts + GTM status - all optional. The
                backend trims these and stores a blank as NULL. */}
            <div className="sm:col-span-2">
              <div className="border-t pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Contacts &amp; status
              </div>
            </div>
            {GTM_FIELDS.filter(({ key }) => key !== "api_key").map(({ key, label, placeholder }) => (
              <Field
                key={key}
                label={label}
                className={key === "comment" ? "sm:col-span-2" : undefined}
              >
                {key === "comment" ? (
                  <textarea
                    value={form[key] ?? ""}
                    onChange={(e) => setField(key, e.target.value)}
                    placeholder={placeholder}
                    rows={2}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                ) : (
                  <Input
                    value={form[key] ?? ""}
                    onChange={(e) => setField(key, e.target.value)}
                    placeholder={placeholder}
                  />
                )}
              </Field>
            ))}

            {STATUS_FIELDS.filter(({ key }) => key === "api_key").map(({ key, label, placeholder }) => (
              <Field key={key} label={label}>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    autoComplete="off"
                    value={apiKeyDraft}
                    onChange={(e) => {
                      setApiKeyDraft(e.target.value)
                      setApiKeyRemove(false)
                    }}
                    placeholder={placeholder}
                    className="flex-1"
                  />
                  {isEdit && ((editing as Studio).api_key_set || apiKeyDraft) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setApiKeyDraft("")
                        setApiKeyRemove(true)
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                {isEdit && (editing as Studio).api_key_set && !apiKeyRemove && !apiKeyDraft && (
                  <p className="text-xs text-muted-foreground">
                    A key is set. Leave blank to keep, or Remove to clear.
                  </p>
                )}
              </Field>
            ))}

            <div className="flex flex-wrap items-center gap-6 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_active)}
                  onChange={(e) => setField("is_active", e.target.checked)}
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(form.api_booking_enabled)}
                  onChange={(e) => setField("api_booking_enabled", e.target.checked)}
                />
                API booking enabled
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving || readOnly}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add studio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV import preview — every change is shown before anything is written */}
      <ImportPreviewDialog
        preview={importPreview}
        busy={importing}
        onCancel={() => { setImportPreview(null); setImportRows([]) }}
        onApply={() => void applyImport()}
      />

      {/* Delete confirmation */}
      {deleteTarget && (
        <SafetyDialog
          title="Delete studio?"
          description={`This permanently removes "${deleteTarget.name}" from the studios table. To take it out of the booking pool without losing its config, deactivate it instead.`}
          warnings={["This cannot be undone."]}
          confirmLabel="Delete"
          variant="destructive"
          typeToConfirm={deleteTarget.name}
          onConfirm={() => void handleDelete()}
          onCancel={() => setDeleteTarget(null)}
          isLoading={deleting}
        />
      )}
    </div>
  )
}

// --- CSV import --------------------------------------------------------------

const GTM_LABELS: Record<string, string> = Object.fromEntries(
  [...GTM_FIELDS, { key: "api_key_set", label: "API key" }].map((f) => [f.key, f.label]),
)

function formatImportChange(col: string, change: { from?: unknown; to?: unknown }) {
  if (col === "api_key_set") {
    const from = change.from ? "Set" : "empty"
    const to = change.to ? "Set" : "empty"
    return { from, to }
  }
  return {
    from: change.from ? String(change.from) : "",
    to: change.to ? String(change.to) : "",
  }
}

/** Read-the-diff-before-you-write dialog for the studio CSV import.
 *
 * The backend decides what a sheet means; this only renders its plan. Showing
 * every old -> new pair is what makes an overwrite safe to allow: the GTM sheet
 * is the source of truth for these columns, so a populated cell DOES replace a
 * populated column, and a human sees each replacement before confirming. Blank
 * cells never reach here — the backend drops them, so they cannot erase data. */
function ImportPreviewDialog({
  preview,
  busy,
  onCancel,
  onApply,
}: {
  preview: StudioImportResult | null
  busy: boolean
  onCancel: () => void
  onApply: () => void
}) {
  if (!preview) return null
  const { updates, unchanged, creates, flagged } = preview
  const writes = updates.length + creates.length

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import preview — nothing written yet</DialogTitle>
        </DialogHeader>

        <div className="space-y-1 text-sm">
          <p>
            <strong>{updates.length}</strong> {updates.length === 1 ? "studio" : "studios"} to
            update, <strong>{creates.length}</strong> to add,{" "}
            <strong>{unchanged.length}</strong> already current,{" "}
            <strong>{flagged.length}</strong> need a human.
          </p>
          <p className="text-muted-foreground">
            Matched on studio name (capitalization, punctuation and spacing are
            ignored). On a studio that already exists, its name is never changed,
            blank cells are skipped, and only the GTM columns — contacts, API
            status, API key, social media status — can be written. A name that
            matches nothing here is added as a new studio, switched off until
            someone fills in its booking system and URL.
          </p>
        </div>

        <div className="max-h-[45vh] space-y-3 overflow-y-auto pr-1">
          {updates.map((row) => (
            <div key={row.studio_id} className="rounded-md border p-3 text-sm">
              <div className="font-medium">{row.studio_name}</div>
              {row.sheet_name !== row.studio_name && (
                <div className="text-xs text-muted-foreground">
                  from “{row.sheet_name}” in the sheet ({row.match})
                </div>
              )}
              <ul className="mt-2 space-y-1">
                {Object.entries(row.changes ?? {}).map(([col, change]) => {
                  const { from, to } = formatImportChange(col, change)
                  return (
                  <li key={col} className="flex flex-wrap items-baseline gap-2">
                    <span className="text-muted-foreground">{GTM_LABELS[col] ?? col}:</span>
                    {from ? (
                      <span className="line-through opacity-60">{from}</span>
                    ) : (
                      <span className="italic opacity-60">empty</span>
                    )}
                    <span className="text-muted-foreground">→</span>
                    <span className="font-medium">{to}</span>
                  </li>
                  )
                })}
              </ul>
            </div>
          ))}

          {creates.length > 0 && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
              <div className="font-medium">
                New studios — {creates.length === 1 ? "this one is" : "these are"} not in the
                table yet
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Added switched OFF, so members and the booking agent never see them.
                Open each one here afterwards, fill in its booking system, URL and
                address, then set it active.
              </p>
              <ul className="mt-2 space-y-1">
                {creates.map((row, i) => (
                  <li key={`${row.sheet_name}-${i}`}>
                    <span className="font-medium">{row.studio_name}</span>{" "}
                    <span className="text-muted-foreground">
                      — {String(row.values?.activity_type ?? "")}
                      {row.values?.address ? `, ${row.values.address}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {flagged.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
              <div className="font-medium">
                Not matched — nothing will be written for these
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Each of these looks close to a studio already in the table, or is
                missing something a new studio needs. Fix the spelling in the sheet
                (or add the studio here by hand) and import again.
              </p>
              <ul className="mt-2 space-y-1">
                {flagged.map((row, i) => (
                  <li key={`${row.sheet_name}-${i}`}>
                    <span className="font-medium">{row.sheet_name}</span>{" "}
                    <span className="text-muted-foreground">— {row.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {writes === 0 && flagged.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Every row in that sheet already matches what is in the table.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onApply} disabled={busy || writes === 0}>
            {busy
              ? "Importing…"
              : creates.length > 0
                ? `Update ${updates.length}, add ${creates.length}`
                : `Update ${updates.length} ${updates.length === 1 ? "studio" : "studios"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- filter + column controls ------------------------------------------------

/** Checkbox dropdown. Hand-rolled rather than reaching for the (unused, never
 *  exercised) ui/dropdown-menu wrapper: this is a button, a list of native
 *  checkboxes and a click-away overlay. */
function MultiSelect({
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string
  options: { value: string; label: string }[]
  selected: Set<string>
  onToggle: (value: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const count = selected.size

  return (
    <div className="relative">
      <Button
        variant={count > 0 ? "secondary" : "outline"}
        size="sm"
        onClick={() => setOpen((o) => !o)}
        disabled={options.length === 0}
      >
        {label}
        {count > 0 && (
          <span className="rounded-full bg-background px-1.5 text-xs tabular-nums">
            {count}
          </span>
        )}
        <ChevronDown className="size-3.5 opacity-60" />
      </Button>
      {open && (
        <>
          {/* Click-away. Sits under the panel, over everything else. */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-30 mt-1 max-h-72 w-56 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
            {options.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={selected.has(o.value)}
                  onChange={() => onToggle(o.value)}
                />
                <span className="truncate capitalize">{o.label}</span>
              </label>
            ))}
            {count > 0 && (
              <button
                onClick={onClear}
                className="mt-1 w-full border-t px-2 py-1.5 text-left text-xs text-muted-foreground hover:text-foreground"
              >
                Clear {label.toLowerCase()}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/** Left-hand column picker (GTM request items 6 + 7). Checkboxes rather than
 *  drag-and-drop: the request explicitly allows an equivalent add/remove
 *  control, and column ORDER stays fixed by the registry so two admins looking
 *  at the same columns always see the same table. */
function ColumnPanel({
  visible,
  onToggle,
  onReset,
  onClose,
}: {
  visible: Set<string>
  onToggle: (key: string) => void
  onReset: () => void
  onClose: () => void
}) {
  const groups: ColumnGroup[] = ["core", "location", "booking", "contacts"]
  return (
    <div className="w-56 shrink-0 rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Columns
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          title="Hide panel"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="mt-2 space-y-3">
        {groups.map((g) => {
          const cols = COLUMNS.filter((c) => c.group === g && !c.pinned)
          if (cols.length === 0) return null
          return (
            <div key={g} className="space-y-1">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {GROUP_LABEL[g]}
              </div>
              {cols.map((c) => (
                <label
                  key={c.key}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={visible.has(c.key)}
                    onChange={() => onToggle(c.key)}
                  />
                  <span className="truncate">{c.label}</span>
                </label>
              ))}
            </div>
          )
        })}
      </div>

      <Button variant="outline" size="sm" className="mt-3 w-full" onClick={onReset}>
        <RotateCcw className="size-4" /> Reset to default view
      </Button>
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        Name and Actions are always shown. Your selection is remembered in this
        browser.
      </p>
    </div>
  )
}

// --- small form helpers ------------------------------------------------------

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string
  col: SortKey
  sortKey: SortKey
  sortDir: "asc" | "desc"
  onSort: (k: SortKey) => void
}) {
  const active = sortKey === col
  return (
    <th className="px-3 py-2 font-medium">
      <button
        onClick={() => onSort(col)}
        className={cn(
          "-mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground",
          active && "text-foreground",
        )}
        title={`Sort by ${label}`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-40" />
        )}
      </button>
    </th>
  )
}

// Scrape / auto-onboard status for a studio row. scrape_config + class_source_mode
// are scraper-owned READ-ONLY fields (see Studio in lib/api.ts): a non-empty
// scrape_config means the scraper has configured + is scraping this studio, so
// we show its class-source mode + detected platform; an empty/null config means
// it was added but not yet configured, so the scraper will auto-onboard it (detect
// the platform from its URL) on the next run.
function ScrapeCell({ studio }: { studio: Studio }) {
  const cfg = studio.scrape_config
  const configured = cfg != null && Object.keys(cfg).length > 0

  if (!configured) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
        title="The scraper will detect this studio's platform from its URL and start pulling classes on the next run."
      >
        Auto-onboard pending
      </span>
    )
  }

  const mode = (studio.class_source_mode ?? "").toString()
  const platform =
    typeof cfg.platform === "string" && cfg.platform.trim() !== ""
      ? cfg.platform
      : studio.booking_system

  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge className={cn("font-normal", MODE_COLOR[mode] ?? "bg-emerald-100 text-emerald-800")}>
        {MODE_LABEL[mode] || mode || "Configured"}
      </Badge>
      {platform && (
        <span className="text-xs text-muted-foreground">{platform}</span>
      )}
    </div>
  )
}

function Field({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  )
}

function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder?: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  )
}
